import React from "react";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import { spacing, darkGrey, fontWeight, fontSize, grey, orange } from "../theme";
import { useShipping, getShippingPrice, SHIPPING_METHODS } from "../context/ShippingContext";
import { useCart, cartItemIsOnBackorder } from "../context/CartContext";

interface SummaryProps {
  /** Delsumma varor (listpris) */
  totalPrice: number;
  children?: React.ReactNode;
  /** Kupongrabatt */
  totalDiscount?: number;
  /** Paket: negativt = rabatt, positivt = tillägg (jämfört med listpris) */
  packageFeeTotal?: number;
}

const Summary = ({
  totalPrice,
  children,
  totalDiscount,
  packageFeeTotal = 0,
}: SummaryProps) => {
  const { shippingMethod, deliverySelection } = useShipping();
  const { cart } = useCart();
  const hasBackorderLines = cart.some(cartItemIsOnBackorder);
  const afterPackage = Math.max(0, totalPrice + packageFeeTotal);
  const shippingPrice = getShippingPrice(
    shippingMethod,
    afterPackage,
    deliverySelection
  );
  const method = SHIPPING_METHODS.find((m) => m.id === shippingMethod);
  const shippingLabel = deliverySelection?.title || method?.label;
  const couponDiscount = totalDiscount ?? 0;
  const total = afterPackage + shippingPrice - couponDiscount;

  return (
    <Content className="checkout-summary-content">
      {hasBackorderLines && (
        <BackorderNotice role="note">
          Minst en vara är beställningsvara och levereras när den finns i lager.
        </BackorderNotice>
      )}
      <Wrapper>
        <div>Varor</div>
        <div>{totalPrice} kr</div>
      </Wrapper>
      {packageFeeTotal < 0 && (
        <Wrapper>
          <div>Paketrabatt</div>
          <div>{packageFeeTotal} kr</div>
        </Wrapper>
      )}
      {packageFeeTotal > 0 && (
        <Wrapper>
          <div>Pakettillägg</div>
          <div>+{packageFeeTotal} kr</div>
        </Wrapper>
      )}
      <Wrapper>
        <div>Frakt ({shippingLabel})</div>
        <div>{shippingPrice === 0 ? <>Fri frakt!</> : <>{shippingPrice} kr</>}</div>
      </Wrapper>
      {couponDiscount > 0 && (
        <Wrapper>
          <div>Rabattkod</div>
          <div>-{couponDiscount} kr</div>
        </Wrapper>
      )}
      <Divider />
      <Wrapper>
        <TotalText>
          Totalt att betala <VatText>inkl. moms</VatText>
        </TotalText>
        <TotalText>{total} kr</TotalText>
      </Wrapper>
      {children && <ChildrenWrapper>{children}</ChildrenWrapper>}
    </Content>
  );
};

const Content = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "50%",
  maxWidth: "100%",
  justifySelf: "center",
  border: { xy: { style: "solid", color: darkGrey, width: "2px" } },
  padding: { x: spacing.m, y: spacing.s },
  gap: spacing.m,
  boxSizing: "border-box",
  ...media({ maxWidth: "768px" }, {
    width: "calc(100% - 32px)",
    margin: { x: "auto" },
  }),
});

const BackorderNotice = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: 1.45,
  padding: { x: spacing.m, y: spacing.s },
  backgroundColor: "#FFFBF5",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderLeft: { style: "solid", color: orange, width: "3px" },
  borderRadius: "8px",
});

const Wrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
});

const ChildrenWrapper = styled.div({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  gap: spacing.m,
});

const Divider = styled.div({
  borderBottom: { style: "solid", color: darkGrey, width: "2px" },
  width: "100%",
});

const TotalText = styled.div({
  fontWeight: fontWeight.bold,
});

const VatText = styled.span({
  fontWeight: fontWeight.light,
  fontSize: fontSize.s,
});

export default Summary;
